import _ from 'lodash'
import { getCSRFToken } from '@wix/thunderbolt-commons'
import type { PlatformBootstrapData, PlatformSiteConfig, CodePackage } from '@wix/thunderbolt-symbols'
import type { ServiceTopology } from '@wix/thunderbolt-ssr-api'
import type {
	WixCodeAppData,
	GetPlatformSiteConfigParams,
	WixCodeBundlersUrlData,
	AppDefIdToWixCodeAppId,
} from 'thunderbolt-viewer-model-root'
import { parse, UrlWithParsedQuery } from 'url'
import { createDatabindingAppData } from './utils/csmManipulations'
import { getAppsScripts } from './utils/appScriptsResolver'

type ClientSpecMap = {
	[name: string]: any
}

const getWixCodeExperimentsQueryParams = ({
	analyzeImportedNamespaces,
	initPlatformApiProvider,
	getAppDefIdFromPackageName,
	resolveNpmPackageEntryByTarget,
}: {
	analyzeImportedNamespaces: boolean
	initPlatformApiProvider: boolean
	getAppDefIdFromPackageName: boolean
	resolveNpmPackageEntryByTarget: boolean
}) =>
	`analyze-imported-namespaces=${analyzeImportedNamespaces}&init-platform-api-provider=${initPlatformApiProvider}&get-app-def-id-from-package-name=${getAppDefIdFromPackageName}&resolve-npm-package-entry-by-target=${resolveNpmPackageEntryByTarget}`

export function getWixCodeBundlersUrlData(
	wixCodeAppData: WixCodeAppData = { appDefinitionId: 'unknown', instanceId: 'unknown', instance: 'unknown' }, // TODO implement proper error handling
	wixCloudBaseDomain: string,
	metaSiteId: string,
	wixCodeAppId: string,
	previewMode: boolean,
	analyzeImportedNamespaces: boolean,
	initPlatformApiProvider: boolean,
	getAppDefIdFromPackageName: boolean,
	resolveNpmPackageEntryByTarget: boolean,
	isRunCustomElementCodeOfBlocksAppExperimentEnabled: boolean,
	appDefIdToWixCodeAppId: AppDefIdToWixCodeAppId
): WixCodeBundlersUrlData {
	const experimentsParams = getWixCodeExperimentsQueryParams({
		analyzeImportedNamespaces,
		initPlatformApiProvider,
		getAppDefIdFromPackageName,
		resolveNpmPackageEntryByTarget,
	})

	const getWixCodeUrl = (id: string) =>
		`https://bundler.${wixCloudBaseDomain}/${metaSiteId}/${wixCodeAppData.instanceId}/${id}/`
	const queryParams = `${experimentsParams}`

	const appDefIdToWixCodeBundlerUrlData = isRunCustomElementCodeOfBlocksAppExperimentEnabled
		? Object.entries(appDefIdToWixCodeAppId).reduce(
				(acc, [appDefId, refWixCodeAppId]) => ({
					...acc,
					[appDefId]: {
						url: getWixCodeUrl(refWixCodeAppId),
						queryParams,
					},
				}),
				{}
		  )
		: {}

	return {
		url: previewMode
			? `https://${wixCodeAppData.instanceId}.dev.${wixCloudBaseDomain}/`
			: getWixCodeUrl(wixCodeAppId),
		queryParams: previewMode
			? `viewMode=preview&instance=${wixCodeAppData.instance}&gridAppId=${wixCodeAppId}&${experimentsParams}`
			: queryParams,
		appDefIdToWixCodeBundlerUrlData,
	}
}

const extractDisabledPlatformApps = (url: UrlWithParsedQuery) => {
	const {
		query: { disablePlatformApps },
	} = url
	return _.isString(disablePlatformApps)
		? disablePlatformApps
				.split(',')
				.reduce((acc: PlatformSiteConfig['bootstrapData']['disabledPlatformApps'], val) => {
					acc[val] = true
					return acc
				}, {})
		: {}
}

export function getWixCodeAppDataKey(clientSpecMap: ClientSpecMap) {
	return _.findKey(clientSpecMap, ['type', 'siteextension'])
}

export function getClientSpecMapWithWixCodeApps(
	clientSpecMap: ClientSpecMap,
	wixCodeAppDataKey: string,
	serviceTopology: ServiceTopology
) {
	// return original clientSpecMap if is empty or siteextension app doesn't exist
	if (!clientSpecMap[wixCodeAppDataKey!]) {
		return clientSpecMap
	}
	const wixCodeViewerAppUrlBase = serviceTopology.scriptsLocationMap['wix-code-viewer-app']
	const dbsmViewerApp = serviceTopology.scriptsLocationMap['dbsm-viewer-app']

	const wixCodeAppData = clientSpecMap[wixCodeAppDataKey!]
	return {
		...clientSpecMap,
		fakeApplicationId: createDatabindingAppData(wixCodeAppData as any, dbsmViewerApp),
		[wixCodeAppDataKey!]: {
			...wixCodeAppData,
			appFields: {
				platform: {
					viewerScriptUrl: `${wixCodeViewerAppUrlBase}/app.js`,
				},
			},
		},
	}
}

export function buildCodePackagesDataFromClientSpecMap(csm: ClientSpecMap): Array<CodePackage> {
	return _(csm)
		.values()
		.map((spec) =>
			_.map(
				_.filter(
					_.get(spec, 'widgets'),
					(widget) =>
						_.get(widget, ['componentFields', 'gridId']) && _.get(widget, ['componentFields', 'importName'])
				),
				(codePackage) => ({
					importName: codePackage.componentFields.importName,
					gridAppId: codePackage.componentFields.gridId,
					appDefId: spec.appDefinitionId,
				})
			)
		)
		.flatten()
		.value()
}

export function getPlatformSiteConfig({
	hasPlatform,
	rendererModel,
	originalRequest,
	serviceTopology,
	appsUrlData,
	isMobileView,
	externalBaseUrl,
	clientWorkerUrl,
	experiments,
	wixCodeBundlersUrlData,
	requestId,
}: GetPlatformSiteConfigParams): PlatformSiteConfig {
	if (!hasPlatform) {
		// carmi runs this transformation regardless of the shouldLoadSiteConfig predicate. twice.
		// @ts-ignore TODO do not mutate everything all around. return fresh objects.
		return null
	}

	const {
		clientSpecMap,
		wixCodeModel,
		pagesPlatformApplications,
		metaSiteId,
		userId,
		siteMetaData,
		previewMode,
		landingPageId,
		platformControllersOnPage,
		premiumFeatures,
		platformAppsExperiments,
	} = rendererModel
	let requestUrl
	try {
		requestUrl = parse(decodeURIComponent(originalRequest.url), true)
	} catch (ex) {
		requestUrl = parse('', true)
	}

	const wixCodeAppDataKey = getWixCodeAppDataKey(clientSpecMap)
	const wixCodeAppData = wixCodeAppDataKey ? (clientSpecMap[wixCodeAppDataKey] as WixCodeAppData) : null
	const dbsmViewerApp = serviceTopology.scriptsLocationMap['dbsm-viewer-app']
	const wixCodeAppDefinitionId = _.get(wixCodeAppData, 'appDefinitionId')
	const wixCodeInstanceId = _.get(wixCodeAppData, 'instanceId')

	const wixCloudBaseDomain = serviceTopology.wixCloudBaseDomain
	const routersConfigMap = _.get(rendererModel, 'routers.configMap')

	const isChancePlatformOnLandingPage =
		!landingPageId || !platformControllersOnPage
			? true
			: !!platformControllersOnPage[landingPageId] ||
			  _.some(pagesPlatformApplications[landingPageId], (appDefId) => appDefId === wixCodeAppDefinitionId) ||
			  _.some(pagesPlatformApplications.masterPage, (appDefId) => appDefId === wixCodeAppDefinitionId) ||
			  _(clientSpecMap)
					.values()
					.map('widgets')
					.flatMap((widgets) => _.values(widgets))
					.some('componentFields.migratingToOoi')

	const disabledPlatformApps = extractDisabledPlatformApps(requestUrl)

	const csm = getClientSpecMapWithWixCodeApps(clientSpecMap, wixCodeAppDataKey!, serviceTopology)

	const filteredClientSpecMap = _(csm)
		.values()
		.filter((spec) => _.get(spec, ['appFields', 'platform', 'viewerScriptUrl']))
		.keyBy('appDefinitionId')

	const appsSpecData = filteredClientSpecMap
		.mapValues((spec) =>
			_.merge(_.pick(spec, ['appDefinitionId', 'type', 'instanceId', 'appDefinitionName', 'isWixTPA']), {
				isModuleFederated: _(spec.widgets).some('componentFields.isModuleFederatedWorker'),
			})
		)
		.value()

	const blocksAppsData = filteredClientSpecMap
		.pickBy('appFields.platform.studio')
		.mapValues((spec) => {
			const packageImportName = _.find(spec.widgets, 'componentFields.importName')?.componentFields.importName

			return {
				...spec.appFields.platform.studio,
				packageImportName,
			}
		})
		.value()

	const widgetsClientSpecMapData = filteredClientSpecMap
		.mapValues(({ widgets }) =>
			_.mapValues(widgets, (widget) => ({
				widgetName: _.get(widget, 'tpaWidgetId') || _.get(widget, ['appPage', 'id']),
				componentFields: _.pick(widget.componentFields, ['seoUrl']),
			}))
		)
		.value()

	const wixCodePageIds: Record<string, string> = {}
	const isWixCodeAnalyzeImportedNamespacesEnabled = Boolean(
		experiments['specs.thunderbolt.WixCodeAnalyzeImportedNamespaces']
	)
	const isInitPlatformApiProviderEnabled = Boolean(experiments['specs.thunderbolt.InitPlatformApiProvider'])
	const isWixCodeGetAppDefIdFromPackageNameEnabled = Boolean(
		experiments['specs.thunderbolt.WixCodeGetAppDefIdFromPackageName']
	)
	const isWixCodeResolveNpmPackageEntryByTargetEnabled = Boolean(
		experiments['specs.thunderbolt.WixCodeResolveNpmPackageEntryByTarget']
	)

	if (wixCodeAppData && wixCodeModel) {
		_.forEach(pagesPlatformApplications, (apps, pageId) => {
			if (apps.includes(wixCodeAppData.appDefinitionId)) {
				const { url, queryParams } = wixCodeBundlersUrlData
				wixCodePageIds[pageId] = `${url}pages/${pageId}.js${queryParams ? `?${queryParams}` : ''}`
			}
		})
	}

	const bootstrapData: PlatformBootstrapData = {
		isMobileView,
		appsSpecData,
		appsUrlData,
		blocksBootstrapData: {
			blocksAppsData,
			experimentsQueryParams: getWixCodeExperimentsQueryParams({
				analyzeImportedNamespaces: isWixCodeAnalyzeImportedNamespacesEnabled,
				initPlatformApiProvider: isInitPlatformApiProviderEnabled,
				getAppDefIdFromPackageName: isWixCodeGetAppDefIdFromPackageNameEnabled,
				resolveNpmPackageEntryByTarget: isWixCodeResolveNpmPackageEntryByTargetEnabled,
			}),
		},
		window: {
			csrfToken: getCSRFToken(originalRequest.headers.cookie?.[0]),
		},
		location: {
			externalBaseUrl,
			isPremiumDomain: premiumFeatures.includes('HasDomain'),
			metaSiteId,
			userFileDomainUrl: serviceTopology.userFileDomainUrl,
		},
		bi: {
			ownerId: userId,
			isMobileFriendly: siteMetaData.isResponsive || siteMetaData.adaptiveMobileOn,
			isPreview: previewMode,
			requestId,
		},
		platformAPIData: {
			routersConfigMap,
		},
		wixCodeBootstrapData: {
			wixCodeAppDefinitionId,
			wixCodeInstanceId,
			wixCloudBaseDomain,
			dbsmViewerApp,
			wixCodePlatformBaseUrl: serviceTopology.scriptsLocationMap['wix-code-platform'],
			wixCodeModel,
			wixCodePageIds,
			elementorySupport: {
				baseUrl: previewMode
					? `https://${wixCodeInstanceId}.dev.${wixCloudBaseDomain}`
					: `${externalBaseUrl}/_api/wix-code-public-dispatcher-ng/siteview`,
			},
			codePackagesData: buildCodePackagesDataFromClientSpecMap(csm),
		},
		autoFrontendModulesBaseUrl: serviceTopology.scriptsLocationMap['auto-frontend-modules'],
		disabledPlatformApps,
		widgetsClientSpecMapData,
		essentials: {
			appsConductedExperiments: platformAppsExperiments,
		},
	}

	const appsScripts = getAppsScripts(requestUrl, bootstrapData, landingPageId, platformControllersOnPage)

	return {
		landingPageId,
		isChancePlatformOnLandingPage,
		clientWorkerUrl,
		bootstrapData,
		appsScripts,
		debug: {
			disablePlatform: _.has(requestUrl.query, 'disablePlatform'),
			disableSnapshots: _.has(requestUrl.query, 'disableSnapshots'),
			enableSnapshots: _.has(requestUrl.query, 'enableSnapshots'),
		},
	}
}
